import { z } from 'zod';
import { commaSeparated } from '../common/comma-separated';
import { ActivitySubjectTypeSchema } from './activities.schemas';
import {
  ActivityRecordCommunicationTypeSchema,
  ActivityRecordDirectionSchema,
  ActivityRecordKindSchema,
  ActivityRecordStateSchema,
  AppointmentTypeSchema,
} from './activity-records.schemas';
import { refersTo, refersToSubject } from '../common/refers-to';

// The workspace-addressed scoped feed (IMM-776.1) — the primitive the
// Aktivitäten tab renders. Shared with the lead leaf (IMM-776.2), so the client
// merges two arrays of one row type.

export const ACTIVITY_FEED_SEGMENTS = ['UPCOMING', 'PAST'] as const;
export const ActivityFeedSegmentSchema = z.enum(ACTIVITY_FEED_SEGMENTS);
export type ActivityFeedSegment = z.infer<typeof ActivityFeedSegmentSchema>;

export const ACTIVITY_FEED_SCOPES = ['OWN', 'OVERSIGHT', 'WORKSPACE'] as const;
export const ActivityFeedScopeSchema = z.enum(ACTIVITY_FEED_SCOPES);
export type ActivityFeedScope = z.infer<typeof ActivityFeedScopeSchema>;

// The token an `assignees` filter value carries alongside member uuids, for a
// row with no assignee (BL-001: "unassigned" is an explicit NULL branch, never
// the absence of a match).
export const ACTIVITY_FEED_UNASSIGNED_TOKEN = 'UNASSIGNED';

const AssigneeFilterValueSchema = z.union([z.literal(ACTIVITY_FEED_UNASSIGNED_TOKEN), z.uuid()]);

export const ACTIVITY_FEED_ORIGINS = ['ACTIVITY_RECORD', 'LEAD_ACTIVITY'] as const;
export const ActivityFeedOriginSchema = z.enum(ACTIVITY_FEED_ORIGINS);
export type ActivityFeedOrigin = z.infer<typeof ActivityFeedOriginSchema>;

export const ActivityFeedItemSchema = z.object({
  activityId: z.uuid(),
  // Which store the row came from — decides which transition route the row's
  // actions call. Every row this leaf emits carries 'ACTIVITY_RECORD';
  // 'LEAD_ACTIVITY' is IMM-776.2's.
  origin: ActivityFeedOriginSchema,
  kind: ActivityRecordKindSchema,
  appointmentType: AppointmentTypeSchema.nullable(),
  type: ActivityRecordCommunicationTypeSchema.nullable(),
  direction: ActivityRecordDirectionSchema.nullable(),
  content: z.string(),
  state: ActivityRecordStateSchema,
  dueAt: z.iso.datetime().nullable(),
  occurredAt: z.iso.datetime().nullable(),
  // When the activity was cancelled; set only on a CANCELLED row, and null on a row
  // cancelled before IMM-1360.1, which recorded no instant.
  cancelledAt: z.iso.datetime().nullable(),
  // Derived at read time (`state === 'OPEN' && dueAt < now`), never stored.
  overdue: z.boolean(),
  // The instant this read ORDERED the row by: `due_at` on an UPCOMING read,
  // COALESCE(occurred_at, cancelled_at, created_at) on a PAST one. Non-null by
  // construction — which is what lets the browser merge two sources by
  // reproducing their order rather than re-deriving it, and what keeps a
  // CANCELLED row (no occurred_at) orderable at all.
  sortAt: z.iso.datetime(),
  subjectType: ActivitySubjectTypeSchema,
  subjectId: refersToSubject('subjectType', z.uuid()),
  subjectLabel: z.string().nullable(),
  // False ONLY when the lookup owns this subject type and resolved nothing.
  subjectAvailable: z.boolean(),
  organizationId: refersTo('organization', z.uuid()).nullable(),
  assignee: z.object({ memberId: refersTo('member', z.uuid()), name: z.string() }).nullable(),
  canModify: z.boolean(),
});
export type ActivityFeedItem = z.infer<typeof ActivityFeedItemSchema>;

export const ActivityFeedResponseSchema = z.object({
  items: z.array(ActivityFeedItemSchema),
  truncated: z.boolean(),
  scope: ActivityFeedScopeSchema,
  // The caller's OWN open activities in this store, filter-independent. Null on
  // a PAST read.
  ownOpenCount: z.number().int().nullable(),
});
export type ActivityFeedResponse = z.infer<typeof ActivityFeedResponseSchema>;

// The Aktivitäten nav counter's read (IMM-1360.4): the caller's OWN open activities in one store —
// the same count the feed's `ownOpenCount` carries, without reading a single row.
export const ActivityOwnOpenCountResponseSchema = z.object({ count: z.number().int().nonnegative() });
export type ActivityOwnOpenCountResponse = z.infer<typeof ActivityOwnOpenCountResponseSchema>;

// Matches the shipped `ListMemberActivityTasksQuerySchema` cap: the page merges
// this read with the lead read client-side, and two independent cursors cannot
// be merged coherently — every source shares one limit.
export const ACTIVITY_FEED_MAX_LIMIT = 200;

export const ListScopedActivitiesPathParamsSchema = z.object({ workspaceId: z.uuid() });

export const ListScopedActivitiesQuerySchema = z.object({
  segment: ActivityFeedSegmentSchema.optional(),
  from: z.iso.datetime().optional(),
  to: z.iso.datetime().optional(),
  // A Standort filter, already subtree-expanded by the client. Intersected with
  // the caller's visible set before it reaches SQL — never widens it.
  organizationIds: commaSeparated(z.uuid()),
  // Comma-separated uuids and/or the literal `UNASSIGNED`.
  assignees: commaSeparated(AssigneeFilterValueSchema),
  kinds: commaSeparated(ActivityRecordKindSchema),
  communicationTypes: commaSeparated(ActivityRecordCommunicationTypeSchema),
  subjectTypes: commaSeparated(ActivitySubjectTypeSchema),
  limit: z.coerce.number().int().min(1).max(ACTIVITY_FEED_MAX_LIMIT).optional(),
});
export type ListScopedActivitiesQuery = z.infer<typeof ListScopedActivitiesQuerySchema>;

// The lead leaf's query (IMM-776.2): byte-identical to the store's minus
// `subjectTypes` — every row this leaf emits is a LEAD, so `.omit()` GUARANTEES
// the two never drift on a filter either tab gains, rather than a hand-copied
// schema only approximating it.
export const ListScopedLeadActivitiesQuerySchema = ListScopedActivitiesQuerySchema.omit({ subjectTypes: true });
export type ListScopedLeadActivitiesQuery = z.infer<typeof ListScopedLeadActivitiesQuerySchema>;
